import { format, parseISO, subDays } from "date-fns";
import { DateRange, KpiCellConfig, WidgetExplanationContent } from "./types";

export const SESSION_STATUS_LABELS_PT: Record<string, string> = {
    done: "realizada",
    booked: "marcada",
    confirmed: "confirmada",
    awaiting_in_clinic: "à espera na clínica",
    in_session: "em sessão",
    cancelled: "cancelada",
    missed: "falta do cliente",
    staff_unavailable: "indisponibilidade de staff",
};

// Etiquetas pt-PT dos valores de treatments.treatment_status_new (choices do Directus).
export const TREATMENT_STATUS_LABELS_PT: Record<string, string> = {
    all_good: "Ativo",
    done: "Alta clínica",
    client_decided_is_ok: "Alta a pedido",
    sporadic: "Esporádico",
    cliente_gave_up: "Desistência",
    no_treatment: "Sem tratamento",
    only_evaluation: "Apenas Avaliação",
    temporary_break: "Pausa Temporária",
    report_done: "Consulta de Avaliação realizada",
    waiting_report: "Aguarda Relatório",
    waiting_start_of_treatment: "Aguarda início de tratamento",
    thinking: "A pensar",
    they_declined: "Eles recusaram",
    no_answer: "Não atenderam",
    no_show: "Não compareceu",
};

export const treatmentStatusListPt = (statuses: Array<string>) => {
    const labels = statuses.map((status) => `"${TREATMENT_STATUS_LABELS_PT[status] || status}"`);
    const joined = listInPt(labels);
    return joined;
};

export const listInPt = (items: Array<string>) => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    const allButLast = items.slice(0, -1).join(", ");
    const joinedList = `${allButLast} e ${items[items.length - 1]}`;
    return joinedList;
};

export const formatDateRangePt = (range: DateRange) => {
    const firstDay = format(parseISO(range.gte), "dd/MM/yyyy");
    const lastDay = format(subDays(parseISO(range.lt), 1), "dd/MM/yyyy");
    const formattedRange = `de ${firstDay} a ${lastDay}`;
    return formattedRange;
};

type DescribeSessionsCountParams = {
    statuses?: Array<string>;
    services?: Array<number>;
    serviceNames: Record<number, string>;
    range: DateRange;
    clinicLabel?: string | null;
    dateFieldLabel?: string;
    excludesInternal?: boolean;
    extraConditionText?: string;
};

export const describeSessionsCount = ({
    statuses,
    services,
    serviceNames,
    range,
    clinicLabel,
    dateFieldLabel,
    excludesInternal = true,
    extraConditionText,
}: DescribeSessionsCountParams) => {
    const statusText =
        statuses && statuses.length > 0
            ? `com estado ${listInPt(statuses.map((status) => `"${SESSION_STATUS_LABELS_PT[status] || status}"`))}`
            : "com qualquer estado";
    const serviceText =
        services && services.length > 0
            ? ` dos serviços ${listInPt(services.map((serviceId) => serviceNames[serviceId] || `serviço #${serviceId}`))}`
            : "";
    const clinicText = clinicLabel ? `, na clínica ${clinicLabel}` : "";
    const extraText = extraConditionText ? ` ${extraConditionText}` : "";
    const internalText = excludesInternal ? " Exclui sessões internas da equipa." : "";

    const description = `Conta as sessões${serviceText} ${statusText}, com ${dateFieldLabel || "data de início"} ${formatDateRangePt(range)}${clinicText}.${extraText}${internalText}`;
    return description;
};

type DescribeTreatmentsCountParams = {
    statuses?: Array<string>;
    dateFieldLabel?: string;
    range?: DateRange;
    extraConditionText?: string;
};

export const describeTreatmentsCount = ({ statuses, dateFieldLabel, range, extraConditionText }: DescribeTreatmentsCountParams) => {
    const statusText = statuses && statuses.length > 0 ? ` com estado ${treatmentStatusListPt(statuses)}` : "";
    const rangeText = range && dateFieldLabel ? `, com ${dateFieldLabel} ${formatDateRangePt(range)}` : "";
    const extraText = extraConditionText ? ` ${extraConditionText}` : "";

    const description = `Conta os tratamentos${statusText}${rangeText}.${extraText}`;
    return description;
};

type KpiCellExplanationContext = {
    serviceNames: Record<number, string>;
    range: DateRange;
    clinicLabel?: string | null;
};

export const describeKpiCell = (
    cell: KpiCellConfig,
    allCells: Array<KpiCellConfig>,
    context: KpiCellExplanationContext
): WidgetExplanationContent => {
    if (cell.sumOfCells) {
        const sourceLabels = cell.sumOfCells.map((sourceKey) => {
            const sourceCell = allCells.find((candidateCell) => candidateCell.key === sourceKey);
            return `"${sourceCell?.label || sourceKey}"`;
        });
        const explanation = {
            queryDescription: `Soma dos cartões ${listInPt(sourceLabels)}.`,
            businessNote: cell.businessNote,
        };
        return explanation;
    }

    const explanation = {
        queryDescription: describeSessionsCount({
            statuses: cell.match?.statuses,
            services: cell.match?.services,
            serviceNames: context.serviceNames,
            range: context.range,
            clinicLabel: context.clinicLabel,
        }),
        businessNote: cell.businessNote,
    };
    return explanation;
};
